import React, { useState, useEffect } from 'react'
import { Form, Input, Button, Typography, Modal } from 'antd';
import { useNavigate } from "react-router-dom";
import { useSnackbar } from 'notistack';
import { login, requestPasswordReset, resetPassword, updatePasswordOnFirstLogin, verifyTOTPLogin } from '../apiCalls';
import { clearStoredUser, getStoredUser } from '../utils/safeLocalStorage';
import * as Sentry from "@sentry/react";
import { useTranslation } from 'react-i18next';
import { calculatePasswordStrength } from '../utils/passwordUtils';


const Login = () => {

    const { t } = useTranslation();

    const initialState = { email: '', password: '' }

    const [formState, setFormState] = useState(initialState)
    const [showResetModal, setShowResetModal] = useState(false);
    const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [showResetForm, setShowResetForm] = useState(false);
    const [firstLoginEmail, setFirstLoginEmail] = useState('');
    const [passwordStrength, setPasswordStrength] = useState(0);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);
    const [show2FAModal, setShow2FAModal] = useState(false);
    const [totpCode, setTotpCode] = useState('');
    const [totpLoading, setTotpLoading] = useState(false);
    const [form] = Form.useForm();
    const [resetForm] = Form.useForm();
    const [firstLoginForm] = Form.useForm();


    const returnToHome = () => navigate('/');

    useEffect(() => {
        checkIfUserIsLoggedIn();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setInput = (key, value) => {
        setFormState({ ...formState, [key]: value })
    }

    const checkIfUserIsLoggedIn = () => {
        const userData = getStoredUser();
        //check if user is logged in or not
        if (!userData) return;

        // Check if user needs to change password on first login
        if (userData.needsPasswordChange) {
            setFirstLoginEmail(userData.email);
            setShowFirstLoginModal(true);
            return;
        }

        enqueueSnackbar(t('auth.login.success'), { variant: 'success' })
        navigate('/');
    }


    const handleSubmit = () => {
        setLoading(true);

        login(formState)
            .then((data) => {
                setLoading(false);
                if (data?.requires2FA) {
                    setShow2FAModal(true);
                } else {
                    checkIfUserIsLoggedIn();
                }
            })
            .catch((err) => {
                enqueueSnackbar(t('auth.login.error'), { variant: 'error' });
                Sentry.captureException(err);
                setLoading(false);
            });
    };

    const handleTOTPSubmit = async () => {
        if (!totpCode || totpCode.length !== 6) {
            enqueueSnackbar(t('auth.twoFactor.enterCode'), { variant: 'warning' });
            return;
        }
        setTotpLoading(true);
        try {
            await verifyTOTPLogin(totpCode);
            setShow2FAModal(false);
            setTotpCode('');
            checkIfUserIsLoggedIn();
        } catch (err) {
            enqueueSnackbar(err.response?.data || t('auth.twoFactor.invalidCode'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setTotpLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!resetEmail) {
            enqueueSnackbar(t('auth.resetPassword.enterEmail'), { variant: 'warning' });
            return;
        }

        setResetLoading(true);
        try {
            await requestPasswordReset(resetEmail);
            enqueueSnackbar(t('auth.resetPassword.codeSent'), { variant: 'success' });
            setShowResetForm(true);
        } catch (err) {
            enqueueSnackbar(t('auth.resetPassword.sendError'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setResetLoading(false);
        }
    };

    const handleResetPassword = async (values) => {
        setResetLoading(true);
        try {
            await resetPassword(values.resetCode, values.newPassword, values.confirmPassword);
            enqueueSnackbar(t('auth.resetPassword.success'), { variant: 'success' });
            resetForm.resetFields();
            setShowResetModal(false);
            setShowResetForm(false);
            setResetEmail('');
        } catch (err) {
            enqueueSnackbar(err.response?.data || t('auth.resetPassword.error'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setResetLoading(false);
        }
    };

    const handleFirstLoginPasswordChange = async (values) => {
        setLoading(true);
        try {
            await updatePasswordOnFirstLogin(firstLoginEmail, values.newPassword, values.confirmPassword);
            enqueueSnackbar(t('auth.firstLogin.success'), { variant: 'success' });

            // Clear user data and close modal to return to login
            clearStoredUser();
            setShowFirstLoginModal(false);
            firstLoginForm.resetFields();
        } catch (err) {
            enqueueSnackbar(err.response?.data || t('auth.firstLogin.error'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='auth-container' style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{
                background: 'white',
                padding: 40,
                borderRadius: 12,
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                width: '100%',
                maxWidth: '450px'
            }}>
                <div style={{ marginBottom: 24, textAlign: 'center' }}>
                    <Typography.Title level={1} style={{ color: '#1890ff', fontWeight: 600, marginBottom: 0 }}>
                        {t('auth.login.title')}
                    </Typography.Title>
                </div>
                <Form layout="vertical" onFinish={handleSubmit} form={form}>
                    <Form.Item
                        label={t('auth.emailAddress')}
                        required
                    >
                        <Input
                            type="email"
                            placeholder={t('auth.emailAddress')}
                            onChange={event => setInput('email', event.target.value)}
                            value={formState.email}
                            maxLength={254}
                        />
                    </Form.Item>

                    <Form.Item
                        label={t('auth.password')}
                        required
                    >
                        <Input.Password
                            placeholder={t('auth.password')}
                            onChange={event => setInput('password', event.target.value)}
                            value={formState.password}
                            maxLength={128}
                        />
                    </Form.Item>

                    <div style={{ textAlign: 'right', marginBottom: 16 }}>
                        <Typography.Link
                            onClick={() => {
                                setShowResetModal(true);
                                resetForm.resetFields();
                                setShowResetForm(false);
                                setResetEmail('');
                            }}
                        >
                            {t('auth.forgotPassword')}
                        </Typography.Link>
                    </div>

                    <Form.Item style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            disabled={loading}
                            loading={loading}
                            style={{ flex: 1 }}
                            size="large"
                        >
                            {loading ? t('common.loading') : t('auth.login.submit')}
                        </Button>
                        <Button onClick={returnToHome} style={{ flex: 1 }} size="large">
                            Back
                        </Button>
                    </Form.Item>
                </Form>
            </div>

            {/* 2FA Verification Modal */}
            <Modal
                title={t('auth.twoFactor.title')}
                open={show2FAModal}
                onCancel={() => { setShow2FAModal(false); setTotpCode(''); }}
                footer={null}
                centered
                closable={false}
            >
                <Typography.Paragraph>
                    {t('auth.twoFactor.instruction')}
                </Typography.Paragraph>
                <Input
                    placeholder="000000"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    onPressEnter={handleTOTPSubmit}
                    style={{ fontSize: 24, letterSpacing: 8, textAlign: 'center', marginBottom: 16 }}
                    autoFocus
                />
                <Button type="primary" block size="large" loading={totpLoading} onClick={handleTOTPSubmit}>
                    {t('auth.twoFactor.verify')}
                </Button>
                <Button block style={{ marginTop: 8 }} onClick={() => { setShow2FAModal(false); setTotpCode(''); }}>
                    {t('common.cancel')}
                </Button>
            </Modal>

            {/* Forgot Password Modal */}
            <Modal
                title={t('auth.resetPassword.title')}
                open={showResetModal}
                onCancel={() => {
                    setShowResetModal(false);
                    setResetLoading(false);
                    resetForm.resetFields();
                    setPasswordStrength(0);
                    setShowResetForm(false);
                    setResetEmail('');
                }}
                footer={null}
                centered
            >
                {!showResetForm ? (
                    <div>
                        <Typography.Text>{t('auth.resetPassword.emailInstruction')}</Typography.Text>
                        <Form style={{ marginTop: 20 }} onFinish={handleForgotPassword} form={resetForm}>
                            <Form.Item label={t('auth.resetPassword.emailLabel')} required>
                                <Input
                                    type="email"
                                    placeholder={t('auth.resetPassword.emailLabel')}
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    maxLength={254}
                                />
                            </Form.Item>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" loading={resetLoading} block>
                                    {t('auth.resetPassword.sendCode')}
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                ) : (
                    <Form form={resetForm} onFinish={handleResetPassword} layout="vertical" style={{ marginTop: 20 }}>
                        <Typography.Text type="secondary">
                            {t('auth.resetPassword.codeInstruction')}
                        </Typography.Text>

                        <Form.Item
                            label={t('auth.resetPassword.resetCode')}
                            required
                            name="resetCode"
                            style={{ marginTop: 16 }}
                            rules={[{ required: true, message: t('auth.resetPassword.enterCode') }]}
                        >
                            <Input
                                placeholder="Enter reset code"
                            />
                        </Form.Item>

                        <Form.Item
                            label={t('auth.resetPassword.newPassword')}
                            required
                            name="newPassword"
                            rules={[
                                { required: true, message: t('auth.validation.newRequired') },
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();
                                        if (value.length < 8) {
                                            return Promise.reject(new Error(t('auth.validation.passwordMin')));
                                        }
                                        if (!/[A-Z]/.test(value)) {
                                            return Promise.reject(new Error(t('auth.validation.passwordUppercase')));
                                        }
                                        if (!/[a-z]/.test(value)) {
                                            return Promise.reject(new Error(t('auth.validation.passwordLowercase')));
                                        }
                                        if (!/[0-9]/.test(value)) {
                                            return Promise.reject(new Error(t('auth.validation.passwordNumber')));
                                        }
                                        if (!/[!@#$%^&*]/.test(value)) {
                                            return Promise.reject(new Error(t('auth.validation.passwordSpecial')));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <Input.Password
                                placeholder={t('auth.resetPassword.newPassword')}
                                onChange={(e) => setPasswordStrength(calculatePasswordStrength(e.target.value))}
                                maxLength={128}
                            />
                        </Form.Item>
                        <div style={{ marginTop: -12, marginBottom: 16, height: 4, background: '#f0f0f0', borderRadius: 2 }}>
                            <div
                                style={{
                                    height: '100%',
                                    width: `${passwordStrength}%`,
                                    background: passwordStrength < 60 ? '#ff4d4f' : passwordStrength < 100 ? '#faad14' : '#52c41a',
                                    borderRadius: 2,
                                    transition: 'all 0.3s'
                                }}
                            />
                        </div>

                        <Form.Item
                            label={t('auth.resetPassword.confirmPassword')}
                            required
                            name="confirmPassword"
                            rules={[
                                { required: true, message: t('auth.validation.confirmRequired') },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('newPassword') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('auth.validation.passwordsDoNotMatch')));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                placeholder={t('auth.resetPassword.confirmPassword')}
                                maxLength={128}
                            />
                        </Form.Item>

                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={resetLoading} block>
                                {t('auth.resetPassword.resetButton')}
                            </Button>
                        </Form.Item>
                    </Form>
                )}
            </Modal>

            {/* First Login Password Change Modal */}
            <Modal
                title={t('auth.firstLogin.title')}
                open={showFirstLoginModal}
                onCancel={() => {
                    localStorage.removeItem('userDetails');
                    setShowFirstLoginModal(false);
                    setLoading(false);
                    firstLoginForm.resetFields();
                    setPasswordStrength(0);
                }}
                footer={null}
                centered
                closable={false}
            >
                <Typography.Paragraph>
                    {t('auth.firstLogin.instruction')}
                </Typography.Paragraph>

                <Form
                    form={firstLoginForm}
                    onFinish={handleFirstLoginPasswordChange}
                    layout="vertical"
                    style={{ marginTop: 20 }}
                >
                    <Form.Item
                        label={t('auth.firstLogin.newPassword')}
                        required
                        name="newPassword"
                        rules={[
                            { required: true, message: t('auth.validation.newRequired') },
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.resolve();
                                    if (value.length < 8) {
                                        return Promise.reject(new Error(t('auth.validation.passwordMin')));
                                    }
                                    if (!/[A-Z]/.test(value)) {
                                        return Promise.reject(new Error(t('auth.validation.passwordUppercase')));
                                    }
                                    if (!/[a-z]/.test(value)) {
                                        return Promise.reject(new Error(t('auth.validation.passwordLowercase')));
                                    }
                                    if (!/[0-9]/.test(value)) {
                                        return Promise.reject(new Error(t('auth.validation.passwordNumber')));
                                    }
                                    if (!/[!@#$%^&*]/.test(value)) {
                                        return Promise.reject(new Error(t('auth.validation.passwordSpecial')));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input.Password
                            placeholder={t('auth.firstLogin.newPassword')}
                            onChange={(e) => setPasswordStrength(calculatePasswordStrength(e.target.value))}
                            maxLength={128}
                        />
                    </Form.Item>
                    <div style={{ marginTop: -12, marginBottom: 16, height: 4, background: '#f0f0f0', borderRadius: 2 }}>
                        <div
                            style={{
                                height: '100%',
                                width: `${passwordStrength}%`,
                                background: passwordStrength < 60 ? '#ff4d4f' : passwordStrength < 100 ? '#faad14' : '#52c41a',
                                borderRadius: 2,
                                transition: 'all 0.3s'
                            }}
                        />
                    </div>

                    <Form.Item
                        label={t('auth.firstLogin.confirmPassword')}
                        required
                        name="confirmPassword"
                        rules={[
                            { required: true, message: t('auth.validation.confirmRequired') },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t('auth.validation.passwordsDoNotMatch')));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder={t('auth.firstLogin.confirmPassword')} maxLength={128} />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            block
                            size="large"
                        >
                            {t('auth.firstLogin.setPassword')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}

export default Login